/* PulseScrubs Theme JS — Web Components */

/* --- Cart Drawer --- */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.querySelector('.cart-drawer__overlay');
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
    });
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    this.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    document.addEventListener('cart:updated', () => this.refresh());
  }

  toggle() {
    this.classList.contains('is-open') ? this.close() : this.open();
  }

  open() {
    this.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }

  close() {
    this.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.renderCart(cart);
      const gcount = cart.items.filter(i => !(i.product_type === 'Add-on' || i.handle === 'embroidery-personalization')).reduce((n, i) => n + i.quantity, 0);
      this.updateCartCount(gcount);
    } catch (e) { /* silently fail */ }
  }

  renderCart(cart) {
    const itemsEl = this.querySelector('.cart-drawer__items');
    const footerEl = this.querySelector('.cart-drawer__footer');
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '<div class="cart-drawer__empty"><p>Your cart is empty</p><a href="/collections/all" class="btn btn--primary btn--sm">Shop Now</a></div>';
      if (footerEl) footerEl.style.display = 'none';
      this.updateDrawerCount(0);
      return;
    }

    if (footerEl) footerEl.style.display = '';

    const EMB_KEYS = ['Embroidery', 'Placement', 'Name', 'Thread Color', 'Font', 'Second Line', 'Note'];
    const isAddon = (item) => item.product_type === 'Add-on' || item.handle === 'embroidery-personalization';
    const garments = cart.items.filter(i => !isAddon(i));
    const addons = cart.items.filter(isAddon);
    const claimed = new Set();

    const garmentHtml = (item) => {
      const linked = addons.filter(a => a.properties && (a.properties['For'] || a.properties._for) === item.product_title && !claimed.has(a.key));
      linked.forEach(a => claimed.add(a.key));
      const linkedKeys = linked.map(a => a.key).join(',');
      const p = item.properties || {};
      const mergedFee = p['_emb_fee'] ? parseInt(p['_emb_fee'], 10) * 100 * item.quantity : 0;
      const feeTotal = linked.length ? linked.reduce((n, a) => n + a.final_line_price, 0) : mergedFee;
      let embHtml = '';
      if (linked.length || (p['Embroidery'] === 'Yes' && mergedFee)) {
        const line1 = [p['Name'], p['Second Line']].filter(Boolean).join(' \u2014 ');
        const line2 = [p['Placement'], p['Thread Color'] ? p['Thread Color'] + ' thread' : '', p['Font'] ? p['Font'] + ' font' : ''].filter(Boolean).join(' \u00b7 ');
        embHtml = `<div class="cart-item__emb">
          <span class="cart-item__emb-head">Embroidery <span class="cart-item__emb-price">+${this.formatMoney(feeTotal)}</span></span>
          ${line1 ? `<span class="cart-item__emb-line">${line1}</span>` : ''}
          ${line2 ? `<span class="cart-item__emb-line">${line2}</span>` : ''}
          ${p['Note'] ? `<span class="cart-item__emb-line">Note: ${p['Note']}</span>` : ''}
        </div>`;
      }
      const otherProps = Object.entries(p)
        .filter(([k, v]) => v && !k.startsWith('_') && !EMB_KEYS.includes(k))
        .map(([k, v]) => `<span class="cart-item__prop">${k}: ${v}</span>`).join('');
      return `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">
          <a href="${item.url}"><img src="${item.image ? item.image.replace(/(\.[^.]+)$/, '_200x$1') : ''}" alt="${item.title}" loading="lazy"></a>
        </div>
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          <span class="cart-item__variant">${item.variant_title || ''}</span>
          ${otherProps ? `<div class="cart-item__props">${otherProps}</div>` : ''}
          ${embHtml}
          <span class="cart-item__price">${this.formatMoney(item.final_line_price)}</span>
          <div class="cart-item__actions">
            <quantity-selector>
              <div class="quantity-selector">
                <button class="quantity-selector__btn" data-action="decrease" aria-label="Decrease quantity">&minus;</button>
                <input class="quantity-selector__input" type="number" value="${item.quantity}" min="1" data-key="${item.key}" data-sync-keys="${linkedKeys}" aria-label="Quantity">
                <button class="quantity-selector__btn" data-action="increase" aria-label="Increase quantity">&plus;</button>
              </div>
            </quantity-selector>
            <button class="cart-item__remove" data-remove="${item.key}" data-remove-with="${linkedKeys}">Remove</button>
          </div>
        </div>
      </div>`;
    };

    itemsEl.innerHTML = garments.map(garmentHtml).join('');

    // self-clean: silently drop orphaned fee lines (no matching garment)
    const orphanKeys = addons.filter(a => !claimed.has(a.key)).map(a => a.key);
    if (orphanKeys.length && !this._cleaning) {
      this._cleaning = true;
      this.updateItems(orphanKeys, 0).finally(() => { this._cleaning = false; });
    }

    const subtotalEl = footerEl?.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);
    this.updateDrawerCount(garments.reduce((n, g) => n + g.quantity, 0));

    itemsEl.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const extra = (btn.dataset.removeWith || '').split(',').filter(Boolean);
        this.updateItems([btn.dataset.remove, ...extra], 0);
      });
    });
  }

  updateDrawerCount(count) {
    const el = this.querySelector('[data-cart-drawer-count]');
    if (el) el.textContent = count;
  }

  async updateItem(key, quantity) {
    const input = this.querySelector(`input[data-key="${key}"]`);
    const syncKeys = input && input.dataset.syncKeys ? input.dataset.syncKeys.split(',').filter(Boolean) : [];
    return this.updateItems([key, ...syncKeys], quantity);
  }

  async updateItems(keys, quantity) {
    try {
      const updates = {};
      keys.forEach(k => { updates[k] = quantity; });
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      this.refresh();
    } catch (e) { /* silently fail */ }
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  formatMoney(cents) {
    var cur = (window.PulseStore && window.PulseStore.currency) || 'PKR';
    var locale = cur === 'QAR' ? 'ar-QA' : cur === 'AED' ? 'ar-AE' : cur === 'SAR' ? 'ar-SA' : 'en-PK';
    return (cents / 100).toLocaleString(locale, { style: 'currency', currency: cur, minimumFractionDigits: 0 });
  }
}
customElements.define('cart-drawer', CartDrawer);

/* --- Mobile Menu --- */
class MobileMenu extends HTMLElement {
  constructor() {
    super();
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-menu-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
    });
    this.querySelector('.mobile-menu__overlay')?.addEventListener('click', () => this.close());
    this.querySelector('[data-menu-close]')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // Accordion per level: a toggle opens the panel named in aria-controls (or the element after it)
    this.querySelectorAll('[data-submenu-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('aria-controls');
        const target = (id && document.getElementById(id)) || btn.nextElementSibling;
        const open = !btn.classList.contains('is-open');
        if (target) target.classList.toggle('is-open', open);
        btn.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  toggle() {
    this.classList.contains('is-open') ? this.close() : this.open();
  }

  open() {
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const firstLink = this.querySelector('.mobile-menu__drawer a');
    if (firstLink) firstLink.focus();
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}
customElements.define('mobile-menu', MobileMenu);

/* --- Desktop navigation dropdowns ---
   CSS opens a dropdown on hover and on keyboard focus (:focus-within). This keeps aria-expanded in step,
   closes the open dropdown on Escape (focus back on its top link) and keeps a wide panel inside the window. */
(function () {
  const nav = document.querySelector('[data-header-nav]');
  if (!nav) return;
  const fit = (item) => {
    const panel = item.querySelector('.header__dropdown--mega');
    if (!panel) return;
    panel.style.setProperty('--mega-shift', '0px');
    const r = panel.getBoundingClientRect();
    const edge = 16;
    let shift = 0;
    if (r.right > window.innerWidth - edge) shift = window.innerWidth - edge - r.right;
    if (r.left + shift < edge) shift = edge - r.left;
    panel.style.setProperty('--mega-shift', Math.round(shift) + 'px');
  };
  const setOpen = (item, open) => {
    const link = item.querySelector('.header__nav-link');
    if (link) link.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) fit(item);
  };
  // Labels never wrap: if the row is wider than its space, tighten it; if still too wide, use the drawer
  const header = nav.closest('.header');
  const fits = () => {
    const need = Array.from(nav.children).reduce((n, c) => n + c.getBoundingClientRect().width, 0);
    return need <= nav.clientWidth + 1;
  };
  const layout = () => {
    if (!header) return;
    header.classList.remove('header--nav-tight', 'header--nav-collapsed');
    if (getComputedStyle(nav).display === 'none' || fits()) return;
    header.classList.add('header--nav-tight');
    if (!fits()) header.classList.add('header--nav-collapsed');
  };
  layout();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
  if (window.ResizeObserver && nav.parentElement) new ResizeObserver(layout).observe(nav.parentElement);
  else window.addEventListener('resize', layout);

  nav.querySelectorAll('.header__nav-item--has-dropdown').forEach((item) => {
    item.addEventListener('mouseenter', () => { item.classList.remove('is-dismissed'); setOpen(item, true); });
    item.addEventListener('mouseleave', () => {
      item.classList.remove('is-dismissed');
      setOpen(item, !!item.querySelector(':focus-visible'));
    });
    // Keyboard focus only (:focus-visible), so a mouse click that leaves focus on a link does not hold it open
    item.addEventListener('focusin', (e) => {
      if (!item.classList.contains('is-dismissed') && e.target.matches(':focus-visible')) setOpen(item, true);
    });
    item.addEventListener('focusout', (e) => {
      if (item.contains(e.relatedTarget)) return;
      item.classList.remove('is-dismissed');
      setOpen(item, item.matches(':hover'));
    });
    item.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      item.classList.add('is-dismissed');
      setOpen(item, false);
      const link = item.querySelector('.header__nav-link');
      if (link && document.activeElement !== link) link.focus();
    });
  });
})();

/* --- Quantity Selector --- */
class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.quantity-selector__input');
    if (!this.input) return;

    this.querySelector('[data-action="decrease"]')?.addEventListener('click', () => this.update(-1));
    this.querySelector('[data-action="increase"]')?.addEventListener('click', () => this.update(1));
    this.input.addEventListener('change', () => this.handleChange());
  }

  update(delta) {
    const newVal = Math.max(1, parseInt(this.input.value || 1) + delta);
    this.input.value = newVal;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleChange() {
    const val = parseInt(this.input.value);
    if (isNaN(val) || val < 1) this.input.value = 1;

    const key = this.input.dataset.key;
    if (key) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.updateItem(key, parseInt(this.input.value));
    }
  }
}
customElements.define('quantity-selector', QuantitySelector);

/* --- Announcement Bar --- */
class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    const dismissed = sessionStorage.getItem('announcement-dismissed');
    if (dismissed) { this.remove(); return; }

    this.querySelector('[data-announcement-close]')?.addEventListener('click', () => {
      sessionStorage.setItem('announcement-dismissed', 'true');
      this.remove();
    });
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* --- Accordion --- */
class AccordionItem extends HTMLElement {
  constructor() {
    super();
    const trigger = this.querySelector('.accordion__trigger');
    if (!trigger) return;

    trigger.addEventListener('click', () => {
      this.classList.toggle('is-open');
    });
  }
}
customElements.define('accordion-item', AccordionItem);

/* --- Predictive Search --- */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.predictive-search__input');
    this.results = this.querySelector('.predictive-search__results');
    this.timeout = null;

    if (!this.input || !this.results) return;

    document.querySelectorAll('[data-search-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });

    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    this.input.addEventListener('input', () => {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.search(this.input.value), 300);
    });
  }

  open() {
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.input.focus(), 100);
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    this.input.value = '';
    this.results.innerHTML = '';
  }

  async search(query) {
    if (query.length < 2) { this.results.innerHTML = ''; return; }

    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
      const data = await res.json();
      const products = data.resources?.results?.products || [];

      if (products.length === 0) {
        this.results.innerHTML = '<div style="padding:1rem;text-align:center;color:var(--color-text-light)">No results found</div>';
        return;
      }

      this.results.innerHTML = products.map(product => `
        <a href="${product.url}" class="predictive-search__item">
          <div class="predictive-search__item-image">
            ${product.image ? `<img src="${product.image.replace(/(\.[^.]+)$/, '_100x$1')}" alt="${product.title}" loading="lazy">` : ''}
          </div>
          <div>
            <div class="predictive-search__item-title">${product.title}</div>
            <div class="predictive-search__item-price">${product.price}</div>
          </div>
        </a>
      `).join('');
    } catch (e) { /* silently fail */ }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* --- Size Guide Modal --- */
class SizeGuideModal extends HTMLElement {
  constructor() {
    super();
    document.querySelectorAll('[data-size-guide-open]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });
    this.querySelector('[data-modal-close]')?.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => { if (e.target === this) this.close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    this.querySelectorAll('.modal__tab').forEach(tab => {
      tab.addEventListener('click', () => this.switchTab(tab));
    });
  }

  open() {
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  switchTab(tab) {
    this.querySelectorAll('.modal__tab').forEach(t => t.classList.remove('is-active'));
    this.querySelectorAll('.size-chart-panel').forEach(p => p.style.display = 'none');
    tab.classList.add('is-active');
    const panel = this.querySelector(`#${tab.dataset.panel}`);
    if (panel) panel.style.display = 'block';
  }
}
customElements.define('size-guide-modal', SizeGuideModal);

/* --- Global: Add to Cart --- */
async function addToCart(variantId, quantity = 1) {
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) throw new Error('Failed to add to cart');
    document.dispatchEvent(new CustomEvent('cart:updated'));
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.open();
    return true;
  } catch (e) {
    return false;
  }
}
